/**
 * CL-NEG-01 Task 11 §E — OfferCheckoutPage data loading and the payment join.
 *
 * Two separate jobs: loading the transaction the offer already lives on, and running the
 * payment — the negotiation becoming a real, paid order. `submitOfferPayment` mirrors
 * `CartCheckoutPage.duck.js`'s own `submitCartPayment` (initiate → Stripe confirm →
 * confirm-payment), with `cartInitiate` swapped for `offerTransition`, because the offer's line
 * items are already fixed server-side — there is nothing here to speculate line items for, unlike
 * the base template's `CheckoutPage.duck.js`.
 */
import { createSlice } from '@reduxjs/toolkit';

import { types as sdkTypes, createImageVariantConfig } from '../../util/sdkLoader';
import { storableError } from '../../util/errors';
import { addMarketplaceEntities, getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { removePurchasedCartItems, releaseCartLock } from '../../ducks/cart.duck';
import { offerTransition, cartRelease } from '../../util/api';
import { states, transitions } from '../../transactions/transactionProcessPurchase';
import {
  SELLER_PAYOUT_INCOMPLETE,
  isSellerPayoutIncompleteApiError,
} from '../../util/custom/sellerPayoutErrors';

const { UUID } = sdkTypes;

// Same helper as TransactionPage.duck.js / MakeOfferPage.duck.js — copied rather than shared,
// same reasoning both of those files already carry: it is a small, self-contained mapping from
// listing-image config to SDK image-variant params, and every duck that fetches a listing image
// has its own copy.
const getImageVariants = listingImageConfig => {
  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } = listingImageConfig;
  const aspectRatio = aspectHeight / aspectWidth;
  return {
    'fields.image': [
      'variants.square-small',
      'variants.square-small2x',
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
  };
};

const initialState = {
  fetchTransactionInProgress: false,
  fetchTransactionError: null,
  transactionRef: null,
  payment: {
    inProgress: false,
    error: null,
    transaction: null,
  },
};

const slice = createSlice({
  name: 'OfferCheckoutPage',
  initialState,
  reducers: {
    fetchTransactionRequest: state => {
      state.fetchTransactionInProgress = true;
      state.fetchTransactionError = null;
    },
    fetchTransactionSuccess: (state, action) => {
      state.fetchTransactionInProgress = false;
      state.transactionRef = action.payload;
    },
    fetchTransactionError: (state, action) => {
      state.fetchTransactionInProgress = false;
      state.fetchTransactionError = action.payload;
    },
    paymentRequest: state => {
      state.payment.inProgress = true;
      state.payment.error = null;
    },
    paymentSuccess: (state, action) => {
      state.payment.inProgress = false;
      state.payment.transaction = action.payload;
    },
    paymentError: (state, action) => {
      state.payment.inProgress = false;
      state.payment.error = action.payload;
    },
  },
});

export const {
  fetchTransactionRequest,
  fetchTransactionSuccess,
  fetchTransactionError,
  paymentRequest,
  paymentSuccess,
  paymentError,
} = slice.actions;

export default slice.reducer;

// ================ Selectors ================ //

export const selectTransaction = state => {
  const { transactionRef } = state.OfferCheckoutPage;
  const [tx] = getMarketplaceEntities(state, transactionRef ? [transactionRef] : []);
  return tx || null;
};

export const selectPayment = state => state.OfferCheckoutPage.payment;

// ================ Thunks ================ //

/**
 * Every code the offer checkout's join can see, normalised to `{code, listingId?, stripeCode?}`.
 *
 * Three shapes reach here, same rule `CartCheckoutPage.duck.js:370-380` records:
 *   - our own endpoint's refusal, `{data: {errors: [{code, listingId?, title?}]}}`
 *     (`server/api/custom/offer-transition.js`'s `sendCartError`)
 *   - Stripe's own rejection, which arrives bare with `{error: {code}}` or `{type, code}`
 *   - anything else (a network failure, a thrown `Error` with `offerCode` set below)
 *
 * `listingId` is kept, `title` is not — the component names the item from its own already-loaded
 * bundle (the same item list it renders), not from whatever the server happened to echo back.
 */
export const normaliseOfferPaymentError = e => {
  const apiErrors = e?.data?.errors || e?.errors || [];
  const first = Array.isArray(apiErrors) ? apiErrors[0] : null;
  const stripeError = e?.error || (e?.type ? e : null);
  const stripeCode = stripeError?.code;

  // CL-LIST-01 D6: a withdrawn-item refusal names EVERY withdrawn row (offer-transition.js's D4
  // re-check), so every id is kept for the page to list; every other code stays single-item.
  if (first?.code === 'listing-withdrawn') {
    const listingIds = apiErrors
      .filter(err => err?.code === 'listing-withdrawn' && err.listingId)
      .map(err => err.listingId);
    return { code: first.code, listingId: first.listingId, listingIds };
  }

  // CL-CHK-01: a seller whose Stripe onboarding was started but never finished is refused with
  // the generic payment-failed code; only the row's Stripe message tells it apart.
  return isSellerPayoutIncompleteApiError(first)
    ? { code: SELLER_PAYOUT_INCOMPLETE }
    : first
    ? { code: first.code, listingId: first.listingId }
    : stripeCode
    ? { code: 'card-declined', stripeCode }
    : { code: e?.offerCode || 'unknown' };
};

/**
 * The offer becomes a real, payable order (brief §C.3, §D.1 step 6, requirement 14, AC12, AC13).
 */
export const submitOfferPayment = ({
  transactionId,
  transaction,
  shippingDetails,
  stripe,
  card,
  savedPaymentMethodId = null,
  billingDetails,
}) => (dispatch, getState, sdk) => {
  if (!stripe || !(card || savedPaymentMethodId)) {
    return Promise.resolve(dispatch(paymentError({ code: 'card-not-ready' })));
  }
  dispatch(paymentRequest());

  // Which join depends on WHOSE figure is live: a seller counter is accepted by paying it
  // (brief Note 2); an accepted offer is paid from offer-agreed. Read from the transaction,
  // never passed in — a caller that could name the transition could pay from the wrong state.
  const txState = transaction?.attributes?.state;
  const transition =
    txState === `state/${states.OFFER_AGREED}`
      ? transitions.REQUEST_PAYMENT_FROM_AGREED
      : transitions.REQUEST_PAYMENT_FROM_OFFER;

  const authorId = transaction?.attributes?.protectedData?.cart?.sellerId;
  // The rows this payment actually buys — the offer's own frozen snapshot, never "the group".
  const purchasedListingIds = (transaction?.attributes?.protectedData?.cart?.items || [])
    .map(i => i?.listingId)
    .filter(Boolean);

  // CL-NEG-01 post-merge Task 6 (D12). Set the moment the join RESOLVES, because that is the
  // moment this transaction's non-anchor rows are hand-held: `runPaymentJoin` takes them exactly
  // as `cart-initiate.js` does, and writes them to the same `protectedData.cart.heldStock` key.
  // Before the join there is nothing held — and if the join itself fails, the server has already
  // given back whatever it took, which is why this stays false on that path.
  let joined = false;

  // `CartCheckoutPage.duck.js:287`'s `releaseIfHeld`, for the identical reason: Sharetribe's own
  // `expire-payment` releases the ANCHOR fifteen minutes later and knows nothing about the rest,
  // so without this a declined card leaves every other item in the basket unbuyable for PT15M
  // plus up to ten minutes of sweep latency, for a sale that never happened (R4).
  //
  // Swallows its own failure: a release that cannot happen must not replace the reason the buyer
  // is actually being shown. The 10-minute sweep is the backstop, and `/api/cart-release` reads
  // the amounts off the transaction rather than from here, so a repeat is safe.
  const releaseIfHeld = () =>
    joined ? cartRelease({ transactionId }).catch(() => null) : Promise.resolve();

  return offerTransition({
    transactionId,
    transition,
    protectedData: {
      // THE CARRIED FINDING. offer-transition merges whatever protectedData it is given, minus
      // `cart` and `negotiation` (offer-transition.js `callerProtectedData`). cart-initiate.js writes
      // `deliveryMethod: 'shipping'` for exactly this reason: ten screens branch on that key —
      // the seller's "I've shipped this order" button, the tracking modal it opens, the
      // shipping-address block, the activity feed and the buyer's status line — and every one
      // of them takes its unknown-method path without it (src/util/custom/deliveryMethod.js).
      // The offer flow collects a delivery address and prices postage for it, exactly as the
      // basket checkout does; there is no pickup path in.
      deliveryMethod: 'shipping',
      ...(shippingDetails ? { shippingDetails } : {}),
    },
    // No money. The join re-prices from the transaction's own `negotiation` snapshot and reads
    // nothing numeric from this body at all (offer-transition.js `runPaymentJoin`, step (a)).
  })
    .then(response => {
      const tx = response?.data?.data;
      // Before the client secret is read, not after: the stock is held from the instant the join
      // returned, so a transaction that came back without an intent owes those rows back too.
      joined = true;
      const clientSecret =
        tx?.attributes?.protectedData?.stripePaymentIntents?.default
          ?.stripePaymentIntentClientSecret;
      if (!clientSecret) {
        const e = new Error('missing-payment-intent');
        e.offerCode = 'missing-payment-intent';
        throw e;
      }
      const paymentMethod = savedPaymentMethodId
        ? savedPaymentMethodId
        : { billing_details: billingDetails, card };
      return stripe
        .confirmCardPayment(clientSecret, { payment_method: paymentMethod })
        .then(result => {
          // Stripe.js RESOLVES with { error } rather than rejecting — the same trap
          // CartCheckoutPage.duck.js:329-336 documents.
          if (result?.error) throw result.error;
          return result;
        });
    })
    .then(() =>
      sdk.transactions.transition(
        { id: transactionId, transition: 'transition/confirm-payment', params: {} },
        { include: ['booking', 'provider'], expand: true }
      )
    )
    .then(response => {
      dispatch(paymentSuccess(response?.data?.data || null));
      // ONLY here, once the money has actually moved. The offer flow is the one path that can
      // buy a basket without going through submitCartPayment, so nothing else would ever clear
      // it — and the buyer's own purchase would come back on the next availability check as
      // "No longer available — this one has sold".
      //
      // The bought ROWS, not the group (review fix). A bundle offer names every row its group
      // holds, so the group empties and disappears exactly as before; a listing-mode offer names
      // one row and leaves the rest of the buyer's basket with that seller alone — it was never
      // part of this transaction, and destroying it here was a silent data loss with a receipt
      // attached.
      if (authorId) {
        dispatch(removePurchasedCartItems({ authorId, listingIds: purchasedListingIds }));
        dispatch(releaseCartLock({ authorId }));
      }
      return response?.data?.data || null;
    })
    .catch(e =>
      // D12 reversed this. It used to say "NO release on failure — the holds belong to a
      // negotiation that is still alive", and that was right while the OFFER held the stock: a
      // declined card left the offer payable, and giving the basket back would have sold it out
      // from under a buyer about to try another card.
      //
      // An offer holds nothing now. These rows were taken at payment start, by this very
      // request, and they belong to the payment attempt — exactly as a Buy It Now basket's do.
      // So a payment that will not complete gives them back at once, the way
      // `submitCartPayment` does, instead of leaving them unbuyable for PT15M plus the sweep's
      // ten minutes. The buyer cannot retry this transaction anyway: the join has already run,
      // and the server answers the next Pay press with `payment-already-started`.
      releaseIfHeld().then(() => {
        dispatch(paymentError(normaliseOfferPaymentError(e)));
        return null;
      })
    );
};

/**
 * Nothing on the server to load beyond the transaction itself — the shape
 * `MakeOfferPage.duck.js`'s `showTransactionThunk` (:122-153) uses.
 */
export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  const txId = new UUID(params.id);
  dispatch(fetchTransactionRequest());

  return sdk.transactions
    .show(
      {
        id: txId,
        include: ['customer', 'provider', 'listing', 'listing.images'],
        ...getImageVariants(config.layout.listingImage),
      },
      { expand: true }
    )
    .then(response => {
      const listingFields = config?.listing?.listingFields;
      dispatch(addMarketplaceEntities(response, { listingFields }));
      dispatch(fetchTransactionSuccess({ id: response.data.data.id, type: 'transaction' }));
      return response;
    })
    .catch(e => {
      dispatch(fetchTransactionError(storableError(e)));
    });
};
